import React, { useState } from "react";
import {
  Button,
  Menu,
  MenuItem,
  CircularProgress,
  Box,
  Typography,
  Divider,
} from "@mui/material";
import {
  MoreVert as MoreVertIcon,
  CheckCircle as CheckCircleIcon,
  LocalShipping as LocalShippingIcon,
  Done as DoneIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Payment as PaymentIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";

const StatusActionsMenu = ({
  order,
  updatingOrderId,
  handleStatusUpdate,
  handlePaymentStatusUpdate, // New prop for payment updates
  getAvailableActions,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleActionClick = (actionItem) => {
    if (actionItem.type === "payment") {
      handlePaymentStatusUpdate(order.id);
    } else {
      handleStatusUpdate(order.id, actionItem.action, actionItem.newStatus);
    }
    handleClose();
  };

  const getActionIcon = (action) => {
    switch (action.toLowerCase()) {
      case "confirm":
        return <CheckCircleIcon fontSize="small" color="primary" />;
      case "dispatch":
        return <LocalShippingIcon fontSize="small" color="info" />;
      case "deliver":
        return <DoneIcon fontSize="small" color="success" />;
      case "markpaid":
        return <PaymentIcon fontSize="small" color="success" />;
      case "cancel":
        return <CancelIcon fontSize="small" color="error" />;
      default:
        return <HourglassEmptyIcon fontSize="small" />;
    }
  };

  const getActionColor = (action) => {
    switch (action.toLowerCase()) {
      case "confirm":
        return "primary.main";
      case "dispatch":
        return "info.main";
      case "deliver":
      case "markpaid":
        return "success.main";
      case "cancel":
        return "error.main";
      default:
        return "text.primary";
    }
  };

  // Pass order type and payment status to getAvailableActions
  const availableActions = getAvailableActions(
    order.orderStatus,
    order.orderType,
    order.paymentStatus
  );
  const isUpdating = updatingOrderId === order.id;

  return (
    <Box>
      <Button
        variant="outlined"
        size="small"
        onClick={handleClick}
        disabled={isUpdating}
        startIcon={
          isUpdating ? <CircularProgress size={16} /> : <MoreVertIcon />
        }
        sx={{
          fontSize: "12px",
          padding: "4px 12px",
          minWidth: "auto",
          textTransform: "none",
          borderRadius: 2,
          "&:hover": {
            backgroundColor: "primary.50",
            borderColor: "primary.main",
          },
        }}
      >
        {isUpdating ? "Updating..." : "Actions"}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "status-actions-button",
        }}
        PaperProps={{
          sx: {
            minWidth: 180,
            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
            borderRadius: 2,
            mt: 1,
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        {availableActions.length > 0 ? (
          availableActions.map((actionItem, actionIndex) => (
            <MenuItem
              key={actionIndex}
              onClick={() => handleActionClick(actionItem)}
              disabled={isUpdating}
              sx={{
                fontSize: "13px",
                py: 1,
                px: 2,
                "&:hover": {
                  backgroundColor: "grey.50",
                },
                // Add visual distinction for payment actions
                ...(actionItem.type === "payment" && {
                  borderLeft: `3px solid`,
                  borderLeftColor: "success.main",
                  backgroundColor: "success.50",
                }),
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {getActionIcon(actionItem.action)}
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "13px",
                    color: getActionColor(actionItem.action),
                    fontWeight: actionItem.type === "payment" ? 600 : 400,
                  }}
                >
                  {actionItem.label}
                </Typography>
              </Box>
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: "13px", fontStyle: "italic" }}
            >
              No actions available
            </Typography>
          </MenuItem>
        )}

        {availableActions.length > 0 && (
          <div>
            <Divider sx={{ my: 0.5 }} />
            <MenuItem
              onClick={handleClose}
              sx={{ fontSize: "12px", color: "text.secondary" }}
            >
              <Typography variant="body2" sx={{ fontSize: "12px" }}>
                Cancel
              </Typography>
            </MenuItem>
          </div>
        )}
      </Menu>
    </Box>
  );
};

export default StatusActionsMenu;
